import { uiText } from '@/i18n/interface';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { calculateInstallmentAmount } from '@/utils/paymentUtils';
import { PaymentCategory, PaymentDetails } from '@/types/payment';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  payment_status?: string;
}

export const usePaymentInstructionsSender = () => {
  const { toast } = useToast();
  const { profile, impersonation } = useAuth();

  const sendPaymentInstructions = async (
    studentId: string | undefined,
    selectedStudent: string,
    students: Student[],
    categories: PaymentCategory[],
    paymentDetails: PaymentDetails,
    fetchStudentPayments: () => void,
    fetchStudents: () => void
  ) => {
    const student = studentId
      ? students.find(s => s.id === studentId)
      : students.find(s => s.id === selectedStudent);

    if (!student) {
      toast({
        title: uiText("Error"),
        description: uiText("Please select a student"),
        variant: "destructive"
      });
      return;
    }

    const category = categories.find(c => c.name === paymentDetails.category);
    if (!category) {
      toast({
        title: uiText("Error"),
        description: uiText("Please select a payment category"),
        variant: "destructive"
      });
      return;
    }

        let totalAmount = 0;
    switch (paymentDetails.subscriptionType) {
      case '1year':
        totalAmount = category.amounts.oneYear;
        break;
      case '6months':
        totalAmount = category.amounts.sixMonths;
        break;
      case '3months':
        totalAmount = category.amounts.threeMonths || 0;
        break;
      case 'monthly':
        totalAmount = category.amounts.monthly || 0;
        break;
      default:
        totalAmount = category.amounts.oneYear;
    }

    // Convert to cents for database storage
    const totalAmountCents = Math.round(totalAmount * 100);
    const installmentAmountCents = calculateInstallmentAmount(totalAmountCents, parseInt(paymentDetails.paymentPlan));
    // Calculate next due date based on subscription type
      const nextDueDate = new Date();
      if (paymentDetails.subscriptionType === '1year') {
        nextDueDate.setFullYear(nextDueDate.getFullYear() + 1);
      } else if (paymentDetails.subscriptionType === '6months') {
        nextDueDate.setMonth(nextDueDate.getMonth() + 6);
      } else if (paymentDetails.subscriptionType === '3months') {
        nextDueDate.setMonth(nextDueDate.getMonth() + 3);
      } else if (paymentDetails.subscriptionType === 'monthly') {
        nextDueDate.setMonth(nextDueDate.getMonth() + 1);
      }
    const nextDueDateISO = totalAmount === 0 ? null : nextDueDate.toISOString().split('T')[0];

    // Determine the correct dojo_id to use
    let targetDojoId: string | null = null;

    if (impersonation.active && impersonation.dojoId) {
      // Use impersonation dojo_id if active
      targetDojoId = impersonation.dojoId;
    } else if (profile?.dojo_id) {
      // Use profile dojo_id for admins and students
      targetDojoId = profile.dojo_id;
    } else {
      // For super users, use the student's dojo_id
      const studentProfile = await supabase
        .from('profiles')
        .select('dojo_id')
        .eq('id', student.id)
        .single();

      if (studentProfile.data?.dojo_id) {
        targetDojoId = studentProfile.data.dojo_id;
      }
    }

    if (!targetDojoId) {
      toast({
        title: uiText("Error"),
        description: uiText("Cannot determine dojo context for payment instructions"),
        variant: "destructive"
      });
      return;
    }

    try {
      // Check if payment instruction already exists for this student and category
      const { data: existingPayments } = await supabase
        .from('payment_instructions')
        .select('id')
        .eq('student_id', student.id)
        .eq('category_name', paymentDetails.category)
        .eq('status', 'pending');

      if (existingPayments && existingPayments.length > 0) {
        toast({
          title: uiText("Error"),
          description: uiText("Payment instructions already exist for this student and category"),
          variant: "destructive"
        });
        return;
      }

      const { error: insertError } = await supabase
        .from('payment_instructions')
        .insert({
          student_id: student.id,
          category_name: paymentDetails.category,
          subscription_type: paymentDetails.subscriptionType,
          payment_plan: paymentDetails.paymentPlan,
          total_amount: totalAmountCents,
          installment_amount: installmentAmountCents,
          total_installments: parseInt(paymentDetails.paymentPlan),
          amount_paid: totalAmount === 0 ? totalAmountCents : 0,
          dojo_id: targetDojoId,
          installments_paid: totalAmount === 0 ? parseInt(paymentDetails.paymentPlan) : 0,
          status: totalAmount === 0 ? 'complete' : 'pending',
          next_due_date: nextDueDateISO
        });

      if (insertError) {
        console.error('Error saving payment instructions:', insertError);
        toast({
          title: uiText("Error"),
          description: uiText("Failed to save payment instructions: {{value0}}", { value0: insertError.message }),
          variant: "destructive"
        });
        return;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          payment_status: totalAmount === 0 ? 'free' : 'pending'
        })
        .eq('id', student.id);

      if (updateError) {
        console.error('Error updating student payment status:', updateError);
      }

      const { error: emailError } = await supabase.functions.invoke('send-payment-instructions', {
        body: {
          studentEmail: student.email,
          studentName: `${student.first_name} ${student.last_name}`,
          paymentDetails: {
            category: paymentDetails.category,
            subscriptionType: paymentDetails.subscriptionType,
            totalAmount: totalAmountCents,
            installmentAmount: installmentAmountCents,
            paymentPlan: paymentDetails.paymentPlan,
            nextDueDate: nextDueDateISO
          }
        }
      });

      if (emailError) {
        console.error('Error sending email:', emailError);
        toast({
          title: uiText("Warning"),
          description: uiText("Payment instructions saved but email failed to send"),
          variant: "destructive"
        });
      } else {
        toast({
          title: uiText("Success"),
          description: uiText("Payment instructions sent to {{value0}} {{value1}}", { value0: student.first_name, value1: student.last_name }),
        });
      }

      fetchStudentPayments();
      fetchStudents();

    } catch (error) {
      console.error('Error sending payment instructions:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to send payment instructions"),
        variant: "destructive"
      });
    }
  };

  return {
    sendPaymentInstructions
  };
};